import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  List,
  Avatar,
  Select,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserAddOutlined,
  TeamOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { departmentService, userService } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';

const { Title } = Typography;

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [memberModalVisible, setMemberModalVisible] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [form] = Form.useForm();
  const [memberForm] = Form.useForm();
  const { can } = usePermissions();

  const canEdit = can('departments', 'edit');
  const canDelete = can('departments', 'delete');

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const data = await departmentService.getAll(true);
      setDepartments(data);
    } catch (error) {
      message.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, []);

  const handleCreateEdit = async (values) => {
    try {
      if (editingDepartment) {
        await departmentService.update(editingDepartment.id, values);
        message.success('Department updated successfully');
      } else {
        await departmentService.create(values);
        message.success('Department created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingDepartment(null);
      fetchDepartments();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (deptId) => {
    try {
      await departmentService.delete(deptId);
      message.success('Department deleted successfully');
      fetchDepartments();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to delete department');
    }
  };

  const handleAddMember = async (values) => {
    try {
      await departmentService.addMember(selectedDepartment.id, values);
      message.success('Member added successfully');
      setMemberModalVisible(false);
      memberForm.resetFields();
      fetchDepartments();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (deptId, userId) => {
    try {
      await departmentService.removeMember(deptId, userId);
      message.success('Member removed');
      fetchDepartments();
    } catch (error) {
      message.error('Failed to remove member');
    }
  };

  const handleToggleHead = async (deptId, userId, isHead) => {
    try {
      await departmentService.setDepartmentHead(deptId, userId, isHead);
      message.success(isHead ? 'Set as department head' : 'Removed as department head');
      fetchDepartments();
    } catch (error) {
      message.error('Failed to update');
    }
  };

  const openEditModal = (dept) => {
    setEditingDepartment(dept);
    form.setFieldsValue({
      name: dept.name,
      description: dept.description,
      is_active: dept.is_active,
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingDepartment(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalVisible(true);
  };

  const openMemberModal = (dept) => {
    setSelectedDepartment(dept);
    memberForm.resetFields();
    setMemberModalVisible(true);
  };

  const getAvailableUsers = () => {
    if (!selectedDepartment) return users;
    const memberIds = selectedDepartment.members?.map((m) => m.user_id) || [];
    return users.filter((u) => !memberIds.includes(u.id));
  };

  const columns = [
    {
      title: 'Department',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <TeamOutlined />
          <span>{name}</span>
          {!record.is_active && <Tag color="red">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Members',
      key: 'members',
      render: (_, record) => (
        <Tag>{record.members?.length || 0} members</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canEdit && (
            <>
              <Tooltip title="Add Member">
                <Button
                  type="text"
                  icon={<UserAddOutlined />}
                  onClick={() => openMemberModal(record)}
                />
              </Tooltip>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(record)}
                />
              </Tooltip>
            </>
          )}
          {canDelete && (
            <Popconfirm
              title="Delete this department?"
              description="Members will be removed from the department."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete"
              okType="danger"
            >
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => (
    <List
      size="small"
      dataSource={record.members || []}
      locale={{ emptyText: 'No members yet' }}
      renderItem={(member) => {
        const user = users.find((u) => u.id === member.user_id);
        return (
          <List.Item
            actions={
              canEdit
                ? [
                    <Tooltip title={member.is_department_head ? 'Remove as Head' : 'Set as Head'}>
                      <Switch
                        size="small"
                        checked={member.is_department_head}
                        onChange={(checked) =>
                          handleToggleHead(record.id, member.user_id, checked)
                        }
                        checkedChildren={<CrownOutlined />}
                      />
                    </Tooltip>,
                    <Popconfirm
                      title="Remove this member?"
                      onConfirm={() => handleRemoveMember(record.id, member.user_id)}
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>,
                  ]
                : []
            }
          >
            <List.Item.Meta
              avatar={<Avatar icon={<TeamOutlined />} />}
              title={
                <Space>
                  {user?.full_name || `User ${member.user_id}`}
                  {member.is_department_head && (
                    <Tag color="gold" icon={<CrownOutlined />}>
                      Head
                    </Tag>
                  )}
                </Space>
              }
              description={user?.email}
            />
          </List.Item>
        );
      }}
    />
  );

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Title level={4} style={{ margin: 0 }}>Departments</Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchDepartments}>
              Refresh
            </Button>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                New Department
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={departments}
          rowKey="id"
          loading={loading}
          expandable={{ expandedRowRender }}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Department Modal */}
      <Modal
        title={editingDepartment ? 'Edit Department' : 'Create Department'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingDepartment(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateEdit}>
          <Form.Item
            name="name"
            label="Department Name"
            rules={[{ required: true, message: 'Please enter department name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>

          {editingDepartment && (
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingDepartment ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Member Modal */}
      <Modal
        title={`Add Member to ${selectedDepartment?.name}`}
        open={memberModalVisible}
        onCancel={() => {
          setMemberModalVisible(false);
          setSelectedDepartment(null);
          memberForm.resetFields();
        }}
        footer={null}
      >
        <Form form={memberForm} layout="vertical" onFinish={handleAddMember}>
          <Form.Item
            name="user_id"
            label="Select User"
            rules={[{ required: true, message: 'Please select a user' }]}
          >
            <Select
              showSearch
              placeholder="Search users..."
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {getAvailableUsers().map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.full_name} ({user.email})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="is_department_head" label="Department Head" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Add Member
              </Button>
              <Button onClick={() => setMemberModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Departments;
